/** @jsx jsx */
import { jsx, css } from "@emotion/react";
import { useMemo, forwardRef } from "react";
import ReactSelect from "react-select";
import AsyncSelect from "react-select/async";

const loadingMessageCSS = css`
    label: loadingMessageCSS;

    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: 48px;
    color: #999999;
    font-weight: 300;
    font-size: 15px;
    line-height: 24px;
`;
const clearIndicatorCSS = css`
    label: clearIndicatorCSS;

    display: flex;
    align-content: center;
    width: 24px;
    margin-right: 12px;
    justify-content: center;
    cursor: pointer;

    i {
        font-size: 14px;
        color: #999999;
    }
`;
const dropdownIndicatorCSS = css`
    label: dropdownIndicatorCSS;

    display: flex;
    align-content: center;
    justify-content: center;

    i {
        font-size: 24px;
        color: #999999;
    }
`;
const indicatorsContainerCSS = css`
    label: indicatorsContainerCSS;

    margin-right: 12px;
`;
const noOptionsMessageCSS = css`
    label: noOptionsMessageCSS;

    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: 48px;
    color: #505050;
    font-weight: 300;
    font-size: 15px;
    line-height: 24px;
`;
const inputCSS = css`
    label: inputCSS;

    margin-left: 0;
    font-size: 15px;
    line-height: 24px;
    color: #505050;
`;
export const optionCSS = css`
    label: optionCSS;

    color: #505050;
    font-weight: 300;
    font-size: 15px;
    line-height: 24px;
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 0 16px;
    height: 48px;

    &,
    &:hover {
        background-color: #fff;
    }

    &:not(:last-of-type) {
        border-bottom: 1px solid #d5d5d5;
    }
`;
const optionDisabledCSS = css`
    label: optionDisabledCSS;

    color: #858181;
`;
const optionSelectedCSS = css`
    label: optionSelectedCSS;

    color: #505050;
    background-color: #fff;
`;
export const controlCSS = ({ isDisabled, menuIsOpen }) => css`
    label: controlCSS;

    /* ${menuIsOpen ? "box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25);" : "box-shadow: none;"} */
    box-shadow: none;
    border-radius: 1px;
    position: relative;
    transition: background 0.1s ease, color 0.1s ease;
    z-index: 2;
    position: relative;

    &,
    &:hover {
        border: 1px solid #d5d5d5;
    }
`;
const singleValueCSS = css`
    label: singleValueCSS;

    margin-left: 0;
    font-size: 15px;
    line-height: 24px;
    color: #505050;
`;
export const valueContainerCSS = css`
    label: valueContainerCSS;

    padding-left: 16px;
`;
const placeholderCSS = css`
    label: placeholderCSS;

    color: #999999;
    font-size: 15px;
    line-height: 24px;
    margin-left: 0;
`;
export const menuCSS = css`
    label: menuCSS;

    background-color: #ffffff;
    border-bottom: 1px solid #d5d5d5;
    border-left: 1px solid #d5d5d5;
    border-radius: 0 0 4px 4px;
    border-right: 1px solid #d5d5d5;
    /* box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25); */
    box-shadow: none;
    margin-bottom: 0;
    margin-top: 0;
    text-align: left;
`;
const menuListCSS = css`
    label: menuListCSS;

    padding-bottom: 0;
    padding-top: 0;
`;
const reactSelectStyles = {
    loadingMessage: (provided, state) => [provided, loadingMessageCSS],
    noOptionsMessage: (provided, state) => [provided, noOptionsMessageCSS],
    indicatorsContainer: (provided, state) => [provided, indicatorsContainerCSS],
    input: (provided, state) => [provided, inputCSS],
    singleValue: (provided, state) => [provided, singleValueCSS],
    valueContainer: (provided, state) => [provided, valueContainerCSS],
    placeholder: (provided, state) => [provided, placeholderCSS],
    control: (provided, state) => [provided, controlCSS(state)],
    option: (provided, state) => [
        provided,
        optionCSS,
        state.isDisabled ? optionDisabledCSS : null,
        state.isSelected ? optionSelectedCSS : null,
    ],
    menu: (provided, state) => [provided, menuCSS],
    menuList: (provided, state) => [provided, menuListCSS],
};

const IndicatorSeparator = () => null;
const DropdownIndicator = state => {
    if (state.selectProps.hideIndicator) {
        return null;
    }
    const menuIsOpen = state.selectProps.menuIsOpen;
    const iconClassName = `trpr__icon-${menuIsOpen ? "next" : "prev"}`;
    return (
        <span css={dropdownIndicatorCSS}>
            <i className={iconClassName} />
        </span>
    );
};
const ClearIndicator = props => {
    const { innerRef, innerProps } = props;
    return (
        <span ref={innerRef} {...innerProps} css={clearIndicatorCSS}>
            <i className="trpr__icon-close" />
        </span>
    );
};

const Select = forwardRef(({ async, components, styles, ...props }, ref) => {
    const _styles = useMemo(() => {
        return {
            ...reactSelectStyles,
            ...(styles || {}),
        };
    }, [styles]);
    if (async) {
        return (
            <AsyncSelect
                ref={ref}
                components={{ IndicatorSeparator, DropdownIndicator, ClearIndicator, ...components }}
                styles={_styles}
                isClearable={true}
                {...props}
                theme={_theme => ({
                    ..._theme,
                    borderRadius: 0,
                    spacing: {
                        ..._theme.spacing,
                        controlHeight: 48,
                    },
                    colors: {
                        ..._theme.colors,
                        primary: "#d5d5d5",
                        primary25: "#8E8DF7",
                        primary50: "#7C79F6",
                        primary75: "#6966F4",
                    },
                })}
            />
        );
    }
    return (
        <ReactSelect
            ref={ref}
            components={{ IndicatorSeparator, DropdownIndicator, ClearIndicator, ...components }}
            styles={reactSelectStyles}
            isClearable={true}
            {...props}
            theme={_theme => ({
                ..._theme,
                borderRadius: 0,
                spacing: {
                    ..._theme.spacing,
                    controlHeight: 48,
                },
                colors: {
                    ..._theme.colors,
                    primary: "#d5d5d5",
                    primary25: "#8E8DF7",
                    primary50: "#7C79F6",
                    primary75: "#6966F4",
                },
            })}
        />
    );
});

export default Select;
